import { format, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import ProductImage from '@v2/shared/components/ProductImage';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import Status from './Status';
import { AutoRenewal } from './types';

const DATE_FORMAT = 'MMMM d, yyyy';

/** How wide the cover's column is on each size of screen, measured on DiscountMags. */
const COVER_WIDTHS = [
  { media: '(min-width: 1280px)', width: 80 },
  { media: '(min-width: 768px)', width: 98 },
  { width: 76 },
];

/**
 * `parseISO`, not `new Date`.
 *
 * These dates come off JMS, whose default is `DateTime::ISO8601` — an offset with no
 * colon in it, `2026-11-04T00:00:00-0500`. That shape is outside the format ECMA-262
 * requires `Date.parse` to accept, so every engine is free to decide for itself, and one
 * that declines returns Invalid Date. date-fns would then throw RangeError from the
 * middle of a render, and with no error boundary over this list the whole account page
 * would go blank rather than one line going missing. `parseISO` reads both spellings
 * itself and does not ask the engine.
 */
function toDate(value: string): Date {
  return parseISO(value);
}

/**
 * The date a customer is told about, which is not always the same date, and is sometimes
 * no date at all.
 *
 * A live subscription is described by when it will renew next; one that has stopped, by
 * when it last did. A subscription that needs attention gets neither, as at the source —
 * it has a charge date, but it is the date of a charge that failed, and calling that
 * either a renewal or a next bill would be a lie. The row says what is wrong with it
 * instead, which is the thing to act on.
 *
 * One deliberate difference from headless_jshop: it required *both* dates to be present
 * before printing either, so a subscription that had never been charged showed nothing
 * where its renewal date should be. Each branch already tests the date it prints, so that
 * outer test only ever suppressed a line it had.
 */
function renewalLine(autoRenewal: AutoRenewal): string | undefined {
  if (['active', 'charged'].includes(autoRenewal.status)) {
    return autoRenewal.automatic_charge_date
      ? `Renews on ${format(toDate(autoRenewal.automatic_charge_date), DATE_FORMAT)}`
      : undefined;
  }

  if (['canceled', 'no-longer-available'].includes(autoRenewal.status)) {
    return autoRenewal.last_charge_date
      ? `Last renewed on ${format(toDate(autoRenewal.last_charge_date), DATE_FORMAT)}`
      : undefined;
  }

  return undefined;
}

type Props = {
  autoRenewal: AutoRenewal;
  /** The per-subscription page's url with `:id` still in it — see Props.manageUrlTemplate. */
  manageUrlTemplate: string;
};

/**
 * One subscription, as a link to the page that manages it.
 *
 * The url arrives as a template rather than being written out here, which is what
 * headless_jshop did too (`urlTemplates.account.autoRenewals.view`). It has to: the id is
 * the customer's own and only exists once the api has answered, so the server cannot
 * attach a finished url to it — but the path around that id is the brand's, and every
 * brand spells it differently.
 */
export default function ListItem({ autoRenewal, manageUrlTemplate }: Props) {
  const { buildUrl } = useUrlGenerator();
  const renewal = renewalLine(autoRenewal);

  return (
    <a
      href={buildUrl(manageUrlTemplate.replace(':id', encodeURIComponent(autoRenewal.id)))}
      className="block p-2 border-b-[1.5px] border-solid border-x-0 border-t-0 border-slate-200 group-last-of-type:border-b-0 no-underline"
    >
      <div className="grid grid-cols-12 gap-3 md:gap-5 p-2 md:p-3 group-hover:bg-slate-100 group-active:bg-slate-200 rounded">
        <span className="col-span-3 md:col-span-2 xl:col-span-1">
          <ProductImage product={autoRenewal.product} boxes={COVER_WIDTHS} loading="lazy" className="w-full" />
        </span>

        <span className="col-span-9 md:col-span-10 xl:col-span-11 flex justify-between items-center">
          <span>
            <Status status={autoRenewal.status} />
            <div className="font-semibold text-slate-900">{autoRenewal.product.name}</div>
            <div className="font-medium text-slate-500 text-sm">for {autoRenewal.delivery_address.name}</div>

            {renewal && <div className="font-medium text-slate-600 text-sm italic mt-3">{renewal}</div>}
          </span>

          <span className="flex items-center text-slate-600 group-hover:text-slate-700 group-active:text-slate-900 mr-3 md:mr-5">
            <span className="hidden md:inline-flex mr-2 font-medium">Manage Subscription</span>
            <FontAwesomeIcon className="text-3xl md:text-xl" icon={faAngleRight} />
          </span>
        </span>
      </div>
    </a>
  );
}
